import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Grid,
    Paper,
    Divider,
    IconButton,
    Tooltip,
    Alert,
    CircularProgress,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar,
    Switch,
    FormControlLabel,
    AppBar,
    Toolbar,
    TextField,
} from '@mui/material';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import TelegramIcon from '@mui/icons-material/Telegram';
import LanguageIcon from '@mui/icons-material/Language';
import DeleteIcon from '@mui/icons-material/Delete';
import CampaignIcon from '@mui/icons-material/Campaign';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import TimerIcon from '@mui/icons-material/Timer';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import MenuIcon from '@mui/icons-material/Menu';
import axios from 'axios';
import {
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
} from '@mui/material';
import { supabase } from './supabaseClient';
import LeftNavDrawer from './components/LeftNavDrawer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://chatai.comturk.com';
const CHATWOOT_URL = 'https://crm1.comturk.com';

const CHANNEL_CONFIG = {
    widget: {
        name: 'Web Widget',
        icon: LanguageIcon,
        color: '#4F46E5',
        description: 'Embed an AI chatbot on your website to help visitors instantly',
        widget: true,  // Special handling - navigate to widget settings
    },
    instagram: {
        name: 'Instagram',
        icon: InstagramIcon,
        color: '#E4405F',
        description: 'Connect your Instagram Business account to receive and reply to DMs',
        native: true,  // Uses native OAuth
    },
    facebook: {
        name: 'Facebook',
        icon: FacebookIcon,
        color: '#1877F2',
        description: 'Connect your Facebook Page to handle Messenger conversations',
        native: true,  // Uses native OAuth (same as Instagram)
    },
    whatsapp: {
        name: 'WhatsApp',
        icon: WhatsAppIcon,
        color: '#25D366',
        description: 'Connect WhatsApp Business API for customer messaging',
        native: true,  // Uses manual token entry modal
    },
    telegram: {
        name: 'Telegram',
        icon: TelegramIcon,
        color: '#0088CC',
        description: 'Connect a Telegram bot to handle conversations',
        native: false,
    },
};

function ChannelsSettings() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [channels, setChannels] = useState([]);
    const [nativeInstagramChannels, setNativeInstagramChannels] = useState([]);
    const [nativeWhatsAppChannels, setNativeWhatsAppChannels] = useState([]);
    const [nativeFacebookChannels, setNativeFacebookChannels] = useState([]);
    const [widgetConfigId, setWidgetConfigId] = useState(null);
    const [widgetConfig, setWidgetConfig] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Campaign state
    const [campaigns, setCampaigns] = useState([]);
    const [campaignDialogOpen, setCampaignDialogOpen] = useState(false);
    const [editingCampaign, setEditingCampaign] = useState(null);
    const [campaignFormData, setCampaignFormData] = useState({
        name: '',
        delay_seconds: 5,
        popup_message: '',
        is_active: true
    });
    const [deletingCampaign, setDeletingCampaign] = useState(null);
    const [campaignsLoading, setCampaignsLoading] = useState(false);
    const [connectDialogOpen, setConnectDialogOpen] = useState(false);
    const [selectedChannelType, setSelectedChannelType] = useState(null);
    const [connectingChannel, setConnectingChannel] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [channelToDelete, setChannelToDelete] = useState(null);
    const [navSidebarOpen, setNavSidebarOpen] = useState(false);
    const [widgetDeleteDialogOpen, setWidgetDeleteDialogOpen] = useState(false);

    // WhatsApp-specific states
    const [whatsappToDelete, setWhatsappToDelete] = useState(null);
    const [whatsappDeleteDialogOpen, setWhatsappDeleteDialogOpen] = useState(false);
    const [whatsappConnectDialogOpen, setWhatsappConnectDialogOpen] = useState(false);
    const [whatsappFormData, setWhatsappFormData] = useState({
        phone_number_id: '',
        waba_id: '',
        display_phone_number: '',
        access_token: ''
    });
    const [whatsappConnecting, setWhatsappConnecting] = useState(false);

    // Facebook-specific states
    const [facebookToDelete, setFacebookToDelete] = useState(null);
    const [facebookDeleteDialogOpen, setFacebookDeleteDialogOpen] = useState(false);

    const getAuthToken = useCallback(async () => {
        const { data: { session } } = await supabase.auth.getSession();
        return session?.access_token;
    }, []);


    // ====== CAMPAIGN FUNCTIONS ======
    const fetchCampaigns = useCallback(async () => {
        if (!widgetConfig) return;
        setCampaignsLoading(true);
        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) return;
            const response = await axios.get(`${BACKEND_URL}/widget/admin/campaigns`, {
                headers: { Authorization: `Bearer ${session.access_token}` }
            });
            setCampaigns(response.data.campaigns || []);
        } catch (error) {
            console.error('Error fetching campaigns:', error);
        } finally {
            setCampaignsLoading(false);
        }
    }, [widgetConfig]);

    useEffect(() => {
        if (widgetConfig) {
            fetchCampaigns();
        }
    }, [widgetConfig, fetchCampaigns]);

    const openCampaignDialog = (campaign = null) => {
        if (campaign) {
            setEditingCampaign(campaign);
            setCampaignFormData({
                name: campaign.name,
                delay_seconds: campaign.delay_seconds,
                popup_message: campaign.popup_message || '',
                is_active: campaign.is_active
            });
        } else {
            setEditingCampaign(null);
            setCampaignFormData({ name: '', delay_seconds: 5, popup_message: '', is_active: true });
        }
        setCampaignDialogOpen(true);
    };

    const saveCampaign = async () => {
        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) return;
            const headers = { Authorization: `Bearer ${session.access_token}` };
            if (editingCampaign) {
                await axios.patch(`${BACKEND_URL}/widget/admin/campaigns/${editingCampaign.id}`, campaignFormData, { headers });
            } else {
                await axios.post(`${BACKEND_URL}/widget/admin/campaigns`, campaignFormData, { headers });
            }
            setCampaignDialogOpen(false);
            fetchCampaigns();
            setSnackbar({ open: true, message: editingCampaign ? 'Campaign updated' : 'Campaign created', severity: 'success' });
        } catch (error) {
            console.error('Error saving campaign:', error);
            setSnackbar({ open: true, message: 'Failed to save campaign', severity: 'error' });
        }
    };

    const toggleCampaign = async (campaign) => {
        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) return;
            await axios.patch(`${BACKEND_URL}/widget/admin/campaigns/${campaign.id}`, 
                { is_active: !campaign.is_active },
                { headers: { Authorization: `Bearer ${session.access_token}` } }
            );
            fetchCampaigns();
        } catch (error) {
            console.error('Error toggling campaign:', error);
        }
    };

    const deleteCampaign = async () => {
        if (!deletingCampaign) return;
        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) return;
            await axios.delete(`${BACKEND_URL}/widget/admin/campaigns/${deletingCampaign.id}`, {
                headers: { Authorization: `Bearer ${session.access_token}` }
            });
            setDeletingCampaign(null);
            fetchCampaigns();
            setSnackbar({ open: true, message: 'Campaign deleted', severity: 'success' });
        } catch (error) {
            console.error('Error deleting campaign:', error);
            setSnackbar({ open: true, message: 'Failed to delete campaign', severity: 'error' });
        }
    };
    // ====== END CAMPAIGN FUNCTIONS ======

    // Fetch linked channels (both native and Chatwoot-based)
    const fetchChannels = useCallback(async () => {
        try {
            setLoading(true);
            const token = await getAuthToken();

            // Fetch Chatwoot-linked channels
            const chatwootResponse = await axios.get(`${BACKEND_URL}/widget/channels`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setChannels(chatwootResponse.data.channels || []);
            setWidgetConfigId(chatwootResponse.data.widget_config_id);

            // Fetch widget config details for AI toggle
            if (chatwootResponse.data.widget_config_id) {
                try {
                    const widgetConfigResp = await axios.get(`${BACKEND_URL}/widget/admin/config`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    setWidgetConfig(widgetConfigResp.data);
                } catch (wErr) {
                    console.log('Could not fetch widget config details:', wErr.message);
                }
            }

            // Fetch native Instagram channels
            try {
                const igResponse = await axios.get(`${BACKEND_URL}/instagram/channels`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setNativeInstagramChannels(igResponse.data.channels || []);
            } catch (igErr) {
                console.log('Native Instagram endpoint not available:', igErr.message);
                setNativeInstagramChannels([]);
            }

            // Fetch native WhatsApp channels
            try {
                const waResponse = await axios.get(`${BACKEND_URL}/whatsapp/channels`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setNativeWhatsAppChannels(waResponse.data.channels || []);
            } catch (waErr) {
                console.log('Native WhatsApp endpoint not available:', waErr.message);
                setNativeWhatsAppChannels([]);
            }

            // Fetch native Facebook channels
            try {
                const fbResponse = await axios.get(`${BACKEND_URL}/facebook/channels`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setNativeFacebookChannels(fbResponse.data.channels || []);
            } catch (fbErr) {
                console.log('Native Facebook endpoint not available:', fbErr.message);
                setNativeFacebookChannels([]);
            }
        } catch (error) {
            console.error('Error fetching channels:', error);
            setSnackbar({ open: true, message: 'Failed to load channels', severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, [getAuthToken]);

    useEffect(() => {
        fetchChannels();
    }, [fetchChannels]);

    // Connect channel - native or Chatwoot based on channel type
    const connectChannel = async (channelType) => {
        const config = CHANNEL_CONFIG[channelType];
        if (!config) return;

        // Navigate to Widget Settings for Web Widget
        if (config.widget) {
            navigate('/widget-settings');
            return;
        }

        // Use native OAuth for Instagram
        if (config.native && channelType === 'instagram') {
            try {
                const token = await getAuthToken();
                const response = await axios.get(`${BACKEND_URL}/instagram/authorize`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                // Redirect to Meta OAuth
                window.location.href = response.data.authorization_url;
            } catch (error) {
                console.error('Error starting Instagram OAuth:', error);
                setSnackbar({ open: true, message: 'Failed to start Instagram connection', severity: 'error' });
            }
            return;
        }

        // Use native modal for WhatsApp (manual token entry)
        if (config.native && channelType === 'whatsapp') {
            setWhatsappConnectDialogOpen(true);
            return;
        }

        // Use native OAuth for Facebook (same pattern as Instagram)
        if (config.native && channelType === 'facebook') {
            try {
                const token = await getAuthToken();
                const response = await axios.get(`${BACKEND_URL}/facebook/authorize`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                // Redirect to Meta OAuth  
                window.location.href = response.data.authorization_url;
            } catch (error) {
                console.error('Error starting Facebook OAuth:', error);
                setSnackbar({ open: true, message: 'Failed to start Facebook connection', severity: 'error' });
            }
            return;
        }

        // Use Chatwoot popup for other channels
        const chatwootPath = `/app/accounts/new/settings/inboxes/new/${channelType}`;
        const popupUrl = `${CHATWOOT_URL}${chatwootPath}`;

        const popup = window.open(
            popupUrl,
            `Connect ${config.name}`,
            'width=800,height=700,scrollbars=yes,resizable=yes'
        );

        if (popup) {
            setConnectingChannel(true);
            setSelectedChannelType(channelType);

            const pollTimer = setInterval(() => {
                if (popup.closed) {
                    clearInterval(pollTimer);
                    setConnectingChannel(false);
                    setTimeout(() => {
                        fetchChannels();
                        setSnackbar({
                            open: true,
                            message: `If you completed the ${config.name} setup in Chatwoot, use the "Manual Link" option to connect it here.`,
                            severity: 'info'
                        });
                    }, 1000);
                }
            }, 1000);
        } else {
            setSnackbar({
                open: true,
                message: 'Popup blocked. Please allow popups for this site.',
                severity: 'warning'
            });
        }
    };

    // Toggle channel active status
    const toggleChannel = async (channelId, currentActive) => {
        try {
            const token = await getAuthToken();
            await axios.patch(
                `${BACKEND_URL}/widget/channels/${channelId}/toggle?is_active=${!currentActive}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setChannels(prev => prev.map(ch =>
                ch.id === channelId ? { ...ch, is_active: !currentActive } : ch
            ));

            setSnackbar({
                open: true,
                message: `Channel ${!currentActive ? 'enabled' : 'disabled'}`,
                severity: 'success'
            });
        } catch (error) {
            console.error('Error toggling channel:', error);
            setSnackbar({ open: true, message: 'Failed to toggle channel', severity: 'error' });
        }
    };

    // Toggle AI for Instagram channel (channel-level AI setting)
    const toggleInstagramAI = async (channelId, currentAiEnabled) => {
        try {
            const token = await getAuthToken();
            await axios.patch(
                `${BACKEND_URL}/instagram/channels/${channelId}/ai-toggle?ai_enabled=${!currentAiEnabled}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setNativeInstagramChannels(prev => prev.map(ch =>
                ch.id === channelId ? { ...ch, ai_enabled: !currentAiEnabled } : ch
            ));

            setSnackbar({
                open: true,
                message: `AI ${!currentAiEnabled ? 'enabled' : 'disabled'} for this channel`,
                severity: 'success'
            });
        } catch (error) {
            console.error('Error toggling Instagram AI:', error);
            setSnackbar({ open: true, message: 'Failed to toggle AI setting', severity: 'error' });
        }
    };

    // Toggle AI for WhatsApp channel (channel-level AI setting)
    const toggleWhatsAppAI = async (channelId, currentAiEnabled) => {
        try {
            const token = await getAuthToken();
            await axios.patch(
                `${BACKEND_URL}/whatsapp/channels/${channelId}/ai-toggle?ai_enabled=${!currentAiEnabled}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setNativeWhatsAppChannels(prev => prev.map(ch =>
                ch.id === channelId ? { ...ch, ai_enabled: !currentAiEnabled } : ch
            ));

            setSnackbar({
                open: true,
                message: `AI ${!currentAiEnabled ? 'enabled' : 'disabled'} for this channel`,
                severity: 'success'
            });
        } catch (error) {
            console.error('Error toggling WhatsApp AI:', error);
            setSnackbar({ open: true, message: 'Failed to toggle AI setting', severity: 'error' });
        }
    };

    // Delete WhatsApp channel
    const deleteWhatsAppChannel = async () => {
        if (!whatsappToDelete) return;

        try {
            const token = await getAuthToken();
            await axios.post(
                `${BACKEND_URL}/whatsapp/disconnect/${whatsappToDelete.id}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setNativeWhatsAppChannels(prev => prev.filter(ch => ch.id !== whatsappToDelete.id));
            setSnackbar({
                open: true,
                message: 'WhatsApp disconnected successfully',
                severity: 'success'
            });
        } catch (error) {
            console.error('Error deleting WhatsApp channel:', error);
            setSnackbar({ open: true, message: 'Failed to disconnect WhatsApp', severity: 'error' });
        } finally {
            setWhatsappDeleteDialogOpen(false);
            setWhatsappToDelete(null);
        }
    };

    // Connect WhatsApp (submit modal form)
    const connectWhatsApp = async () => {
        if (!whatsappFormData.phone_number_id || !whatsappFormData.access_token) {
            setSnackbar({ open: true, message: 'Please fill in Phone Number ID and Access Token', severity: 'error' });
            return;
        }
        setWhatsappConnecting(true);
        try {
            const token = await getAuthToken();
            await axios.post(
                `${BACKEND_URL}/whatsapp/register`,
                whatsappFormData,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setSnackbar({ open: true, message: 'WhatsApp connected successfully!', severity: 'success' });
            setWhatsappConnectDialogOpen(false);
            setWhatsappFormData({ phone_number_id: '', waba_id: '', display_phone_number: '', access_token: '' });
            fetchChannels();
        } catch (error) {
            console.error('Error connecting WhatsApp:', error);
            setSnackbar({ open: true, message: error.response?.data?.detail || 'Failed to connect WhatsApp', severity: 'error' });
        } finally {
            setWhatsappConnecting(false);
        }
    };

    // Toggle AI for Facebook channel
    const toggleFacebookAI = async (channelId, currentAiEnabled) => {
        try {
            const token = await getAuthToken();
            await axios.patch(
                `${BACKEND_URL}/facebook/channels/${channelId}/ai-toggle?ai_enabled=${!currentAiEnabled}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setNativeFacebookChannels(prev => prev.map(ch =>
                ch.id === channelId ? { ...ch, ai_enabled: !currentAiEnabled } : ch
            ));

            setSnackbar({
                open: true,
                message: `AI ${!currentAiEnabled ? 'enabled' : 'disabled'} for this channel`,
                severity: 'success'
            });
        } catch (error) {
            console.error('Error toggling Facebook AI:', error);
            setSnackbar({ open: true, message: 'Failed to toggle AI setting', severity: 'error' });
        }
    };

    // Delete Facebook channel
    const deleteFacebookChannel = async () => {
        if (!facebookToDelete) return;

        try {
            const token = await getAuthToken();
            await axios.post(
                `${BACKEND_URL}/facebook/disconnect/${facebookToDelete.id}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setNativeFacebookChannels(prev => prev.filter(ch => ch.id !== facebookToDelete.id));
            setSnackbar({
                open: true,
                message: 'Facebook Page disconnected successfully',
                severity: 'success'
            });
        } catch (error) {
            console.error('Error deleting Facebook channel:', error);
            setSnackbar({ open: true, message: 'Failed to disconnect Facebook', severity: 'error' });
        } finally {
            setFacebookDeleteDialogOpen(false);
            setFacebookToDelete(null);
        }
    };

    // Toggle AI for Widget (channel-level AI setting)
    const toggleWidgetAI = async () => {
        if (!widgetConfig) return;

        const currentAiEnabled = widgetConfig.ai_enabled !== false;
        try {
            const token = await getAuthToken();
            await axios.patch(
                `${BACKEND_URL}/widget/admin/config/ai-toggle?ai_enabled=${!currentAiEnabled}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setWidgetConfig(prev => ({ ...prev, ai_enabled: !currentAiEnabled }));

            setSnackbar({
                open: true,
                message: `AI ${!currentAiEnabled ? 'enabled' : 'disabled'} for Web Widget`,
                severity: 'success'
            });
        } catch (error) {
            console.error('Error toggling Widget AI:', error);
            setSnackbar({ open: true, message: 'Failed to toggle AI setting', severity: 'error' });
        }
    };

    // Handle logout
    const handleLogout = async () => {
        await supabase.auth.signOut();
        navigate('/login');
    };

    // Delete channel link
    const deleteChannel = async () => {
        if (!channelToDelete) return;

        try {
            const token = await getAuthToken();
            await axios.delete(
                `${BACKEND_URL}/widget/channels/${channelToDelete.id}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setChannels(prev => prev.filter(ch => ch.id !== channelToDelete.id));
            setSnackbar({
                open: true,
                message: `${CHANNEL_CONFIG[channelToDelete.channel_type]?.name || 'Channel'} disconnected`,
                severity: 'success'
            });
        } catch (error) {
            console.error('Error deleting channel:', error);
            setSnackbar({ open: true, message: 'Failed to disconnect channel', severity: 'error' });
        } finally {
            setDeleteDialogOpen(false);
            setChannelToDelete(null);
        }
    };

    // Delete native Instagram channel
    const [instagramToDelete, setInstagramToDelete] = useState(null);
    const [instagramDeleteDialogOpen, setInstagramDeleteDialogOpen] = useState(false);

    const deleteInstagramChannel = async () => {
        if (!instagramToDelete) return;

        try {
            const token = await getAuthToken();
            await axios.post(
                `${BACKEND_URL}/instagram/disconnect/${instagramToDelete.id}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setNativeInstagramChannels(prev => prev.filter(ch => ch.id !== instagramToDelete.id));
            setSnackbar({
                open: true,
                message: 'Instagram disconnected successfully',
                severity: 'success'
            });
        } catch (error) {
            console.error('Error deleting Instagram channel:', error);
            setSnackbar({ open: true, message: 'Failed to disconnect Instagram', severity: 'error' });
        } finally {
            setInstagramDeleteDialogOpen(false);
            setInstagramToDelete(null);
        }
    };

    // Copy webhook URL
    const copyWebhookUrl = (webhookToken) => {
        const url = `${BACKEND_URL}/widget/channel-webhook?token=${webhookToken}`;
        navigator.clipboard.writeText(url);
        setSnackbar({ open: true, message: 'Webhook URL copied!', severity: 'success' });
    };

    // Delete widget configuration
    const deleteWidgetConfiguration = async () => {
        try {
            const token = await getAuthToken();
            await axios.delete(`${BACKEND_URL}/widget/admin/config`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setWidgetConfig(null);
            setWidgetConfigId(null);
            setSnackbar({ open: true, message: 'Web Widget deleted successfully', severity: 'success' });
        } catch (error) {
            console.error('Error deleting widget:', error);
            setSnackbar({ open: true, message: 'Failed to delete widget', severity: 'error' });
        } finally {
            setWidgetDeleteDialogOpen(false);
        }
    };

    // Get connected channel types (combining Chatwoot and native)
    const connectedTypes = [
        ...channels.map(ch => ch.channel_type),
        ...nativeInstagramChannels.map(() => 'instagram'),
        ...nativeWhatsAppChannels.map(() => 'whatsapp'),
        ...nativeFacebookChannels.map(() => 'facebook')
    ];

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
            {/* Top AppBar */}
            <AppBar position="static" color="default" elevation={1}>
                <Toolbar variant="dense">
                    <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
                        Channels
                    </Typography>
                    <IconButton edge="end" color="inherit" aria-label="menu" onClick={() => setNavSidebarOpen(true)}>
                        <MenuIcon />
                    </IconButton>
                </Toolbar>
            </AppBar>

            <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3, flex: 1 }}>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                    Connect social channels to receive and respond to messages from Instagram, Facebook, WhatsApp, and Telegram.
                </Typography>

                {!widgetConfigId && (
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        You need to set up your Widget first before connecting channels. Go to Widget Settings to create your widget.
                    </Alert>
                )}

                {/* Connected Channels */}
                {(channels.length > 0 || nativeInstagramChannels.length > 0 || nativeWhatsAppChannels.length > 0 || nativeFacebookChannels.length > 0) && (
                    <>
                        <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                            Connected Channels
                        </Typography>
                        <Grid container spacing={2} sx={{ mb: 4 }}>
                            {/* Native Instagram Channels */}
                            {nativeInstagramChannels.map((igChannel) => {
                                const config = CHANNEL_CONFIG.instagram;
                                const IconComponent = config.icon;

                                return (
                                    <Grid item xs={12} md={6} key={`ig-${igChannel.id}`}>
                                        <Paper sx={{ p: 2, border: '1px solid', borderColor: 'success.main' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <IconComponent sx={{ color: config.color, fontSize: 32 }} />
                                                    <Box>
                                                        <Typography variant="subtitle1" fontWeight="bold">
                                                            {config.name}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            @{igChannel.instagram_username || igChannel.page_name || 'Connected Account'}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Chip
                                                        size="small"
                                                        label="Active"
                                                        color="success"
                                                    />

                                                    {/* AI Toggle */}
                                                    <Tooltip title={igChannel.ai_enabled !== false ? 'AI is ON - Click to use Manual Mode' : 'AI is OFF - Click to enable AI'}>
                                                        <FormControlLabel
                                                            control={
                                                                <Switch
                                                                    size="small"
                                                                    checked={igChannel.ai_enabled !== false}
                                                                    onChange={() => toggleInstagramAI(igChannel.id, igChannel.ai_enabled !== false)}
                                                                    color="primary"
                                                                />
                                                            }
                                                            label={
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                    {igChannel.ai_enabled !== false ? (
                                                                        <SmartToyIcon fontSize="small" color="primary" />
                                                                    ) : (
                                                                        <PersonIcon fontSize="small" color="warning" />
                                                                    )}
                                                                </Box>
                                                            }
                                                            sx={{ mr: 0 }}
                                                        />
                                                    </Tooltip>

                                                    <Tooltip title="Disconnect Instagram">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => {
                                                                setInstagramToDelete(igChannel);
                                                                setInstagramDeleteDialogOpen(true);
                                                            }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </Box>
                                        </Paper>
                                    </Grid>
                                );
                            })}

                            {/* Native WhatsApp Channels */}
                            {nativeWhatsAppChannels.map((waChannel) => {
                                const config = CHANNEL_CONFIG.whatsapp;
                                const IconComponent = config.icon;

                                return (
                                    <Grid item xs={12} md={6} key={`wa-${waChannel.id}`}>
                                        <Paper sx={{ p: 2, border: '1px solid', borderColor: 'success.main' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <IconComponent sx={{ color: config.color, fontSize: 32 }} />
                                                    <Box>
                                                        <Typography variant="subtitle1" fontWeight="bold">
                                                            {config.name}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {waChannel.display_phone_number || waChannel.verified_name || 'Connected Account'}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Chip
                                                        size="small"
                                                        label="Active"
                                                        color="success"
                                                    />

                                                    {/* AI Toggle */}
                                                    <Tooltip title={waChannel.ai_enabled !== false ? 'AI is ON - Click to use Manual Mode' : 'AI is OFF - Click to enable AI'}>
                                                        <FormControlLabel
                                                            control={
                                                                <Switch
                                                                    size="small"
                                                                    checked={waChannel.ai_enabled !== false}
                                                                    onChange={() => toggleWhatsAppAI(waChannel.id, waChannel.ai_enabled !== false)}
                                                                    color="primary"
                                                                />
                                                            }
                                                            label={
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                    {waChannel.ai_enabled !== false ? (
                                                                        <SmartToyIcon fontSize="small" color="primary" />
                                                                    ) : (
                                                                        <PersonIcon fontSize="small" color="warning" />
                                                                    )}
                                                                </Box>
                                                            }
                                                            sx={{ mr: 0 }}
                                                        />
                                                    </Tooltip>

                                                    <Tooltip title="Disconnect WhatsApp">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => {
                                                                setWhatsappToDelete(waChannel);
                                                                setWhatsappDeleteDialogOpen(true);
                                                            }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </Box>
                                        </Paper>
                                    </Grid>
                                );
                            })}

                            {/* Native Facebook Channels */}
                            {nativeFacebookChannels.map((fbChannel) => {
                                const config = CHANNEL_CONFIG.facebook;
                                const IconComponent = config.icon;

                                return (
                                    <Grid item xs={12} md={6} key={`fb-${fbChannel.id}`}>
                                        <Paper sx={{ p: 2, border: '1px solid', borderColor: 'success.main' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <IconComponent sx={{ color: config.color, fontSize: 32 }} />
                                                    <Box>
                                                        <Typography variant="subtitle1" fontWeight="bold">
                                                            {config.name}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {fbChannel.page_name || 'Facebook Page'}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Chip
                                                        size="small"
                                                        label="Active"
                                                        color="success"
                                                    />

                                                    {/* AI Toggle */}
                                                    <Tooltip title={fbChannel.ai_enabled !== false ? 'AI is ON - Click to use Manual Mode' : 'AI is OFF - Click to enable AI'}>
                                                        <FormControlLabel
                                                            control={
                                                                <Switch
                                                                    size="small"
                                                                    checked={fbChannel.ai_enabled !== false}
                                                                    onChange={() => toggleFacebookAI(fbChannel.id, fbChannel.ai_enabled !== false)}
                                                                    color="primary"
                                                                />
                                                            }
                                                            label={
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                    {fbChannel.ai_enabled !== false ? (
                                                                        <SmartToyIcon fontSize="small" color="primary" />
                                                                    ) : (
                                                                        <PersonIcon fontSize="small" color="warning" />
                                                                    )}
                                                                </Box>
                                                            }
                                                            sx={{ mr: 0 }}
                                                        />
                                                    </Tooltip>

                                                    <Tooltip title="Disconnect Facebook">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => {
                                                                setFacebookToDelete(fbChannel);
                                                                setFacebookDeleteDialogOpen(true);
                                                            }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </Box>
                                        </Paper>
                                    </Grid>
                                );
                            })}

                            {/* Chatwoot-linked Channels */}
                            {channels.map((channel) => {
                                const config = CHANNEL_CONFIG[channel.channel_type];
                                const IconComponent = config?.icon || LinkIcon;

                                return (
                                    <Grid item xs={12} md={6} key={channel.id}>
                                        <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <IconComponent sx={{ color: config?.color, fontSize: 32 }} />
                                                    <Box>
                                                        <Typography variant="subtitle1" fontWeight="bold">
                                                            {config?.name || channel.channel_type}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {channel.channel_identifier || `Inbox #${channel.chatwoot_inbox_id}`}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Chip
                                                        size="small"
                                                        label={channel.is_active ? 'Active' : 'Inactive'}
                                                        color={channel.is_active ? 'success' : 'default'}
                                                    />

                                                    <Tooltip title="Copy Webhook URL">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => copyWebhookUrl(channel.webhook_token)}
                                                        >
                                                            <ContentCopyIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>

                                                    <Tooltip title={channel.is_active ? 'Disable' : 'Enable'}>
                                                        <Switch
                                                            size="small"
                                                            checked={channel.is_active}
                                                            onChange={() => toggleChannel(channel.id, channel.is_active)}
                                                        />
                                                    </Tooltip>

                                                    <Tooltip title="Disconnect">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => {
                                                                setChannelToDelete(channel);
                                                                setDeleteDialogOpen(true);
                                                            }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </Box>
                                        </Paper>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </>
                )}

                {/* Available Channels */}
                <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                    {channels.length > 0 ? 'Add More Channels' : 'Available Channels'}
                </Typography>
                <Grid container spacing={3}>
                    {Object.entries(CHANNEL_CONFIG).map(([type, config]) => {
                        const IconComponent = config.icon;
                        const isConnected = connectedTypes.includes(type);
                        const isWidget = config.widget;
                        const isWidgetConfigured = isWidget && widgetConfigId;
                        const isNativeInstagram = config.native && type === 'instagram';

                        // Web Widget and Instagram are always clickable, other channels need widget to be set up first
                        const isClickable = isWidget || isNativeInstagram || (!isConnected && widgetConfigId);
                        const showAsConfigured = isWidget ? isWidgetConfigured : isConnected;

                        return (
                            <Grid item xs={12} sm={6} md={3} key={type}>
                                <Card
                                    sx={{
                                        height: '100%',
                                        opacity: (!isWidget && !isNativeInstagram && isConnected) ? 0.6 : 1,
                                        cursor: isClickable ? 'pointer' : 'default',
                                        '&:hover': isClickable ? {
                                            boxShadow: 4,
                                            borderColor: config.color,
                                        } : {},
                                        border: '1px solid',
                                        borderColor: showAsConfigured ? 'success.main' : 'divider',
                                    }}
                                    onClick={() => isClickable && connectChannel(type)}
                                >
                                    <CardContent sx={{ textAlign: 'center', py: 3 }}>
                                        <IconComponent
                                            sx={{
                                                fontSize: 48,
                                                color: config.color,
                                                mb: 2,
                                            }}
                                        />
                                        <Typography variant="h6" gutterBottom>
                                            {config.name}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            {config.description}
                                        </Typography>

                                        {isWidget ? (
                                            // Web Widget shows Configure/Configured with AI toggle
                                            isWidgetConfigured ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Chip
                                                        icon={<LinkIcon />}
                                                        label="Configured"
                                                        color="success"
                                                        size="small"
                                                    />
                                                    <Tooltip title={widgetConfig?.ai_enabled !== false ? 'AI is ON - Click to disable' : 'AI is OFF - Click to enable'}>
                                                        <FormControlLabel
                                                            control={
                                                                <Switch
                                                                    size="small"
                                                                    checked={widgetConfig?.ai_enabled !== false}
                                                                    onChange={(e) => {
                                                                        e.stopPropagation();
                                                                        toggleWidgetAI();
                                                                    }}
                                                                    color="primary"
                                                                />
                                                            }
                                                            label={
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                    {widgetConfig?.ai_enabled !== false ? (
                                                                        <SmartToyIcon fontSize="small" color="primary" />
                                                                    ) : (
                                                                        <PersonIcon fontSize="small" color="warning" />
                                                                    )}
                                                                </Box>
                                                            }
                                                            sx={{ mr: 0 }}
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                    </Tooltip>
                                                    <Tooltip title="Delete Widget">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setWidgetDeleteDialogOpen(true);
                                                            }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            ) : (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        connectChannel(type);
                                                    }}
                                                >
                                                    Configure
                                                </Button>
                                            )
                                        ) : isNativeInstagram ? (
                                            // Native Instagram - always clickable
                                            isConnected ? (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    color="primary"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        connectChannel(type);
                                                    }}
                                                >
                                                    Add Account
                                                </Button>
                                            ) : (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        connectChannel(type);
                                                    }}
                                                >
                                                    Connect
                                                </Button>
                                            )
                                        ) : (
                                            // Other channels show Connect/Connected
                                            isConnected ? (
                                                <Chip
                                                    icon={<LinkIcon />}
                                                    label="Connected"
                                                    color="success"
                                                    size="small"
                                                />
                                            ) : (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    disabled={!widgetConfigId || connectingChannel}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        connectChannel(type);
                                                    }}
                                                >
                                                    Connect
                                                </Button>
                                            )
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>

                {/* Info Alert */}
                <Alert severity="info" sx={{ mt: 4 }}>
                    <Typography variant="subtitle2" fontWeight="bold">How it works:</Typography>
                    <Typography variant="body2">
                        1. Click "Connect" to open Chatwoot and set up your social channel<br />
                        2. Complete the OAuth authorization in Chatwoot<br />
                        3. Copy the Agent Bot webhook URL from here to your Chatwoot bot settings<br />
                        4. Messages will now flow through your unified inbox!
                    </Typography>
                </Alert>

                {/* Delete Confirmation Dialog */}
                <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                    <DialogTitle>Disconnect Channel?</DialogTitle>
                    <DialogContent>
                        <Typography>
                            Are you sure you want to disconnect {CHANNEL_CONFIG[channelToDelete?.channel_type]?.name}?
                            Messages from this channel will no longer appear in your inbox.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                        <Button onClick={deleteChannel} color="error" variant="contained">
                            Disconnect
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Instagram Delete Confirmation Dialog */}
                <Dialog open={instagramDeleteDialogOpen} onClose={() => setInstagramDeleteDialogOpen(false)}>
                    <DialogTitle>Disconnect Instagram?</DialogTitle>
                    <DialogContent>
                        <Typography>
                            Are you sure you want to disconnect your Instagram account
                            (@{instagramToDelete?.instagram_username || instagramToDelete?.page_name || 'this account'})?
                            You will no longer receive DMs from this account.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setInstagramDeleteDialogOpen(false)}>Cancel</Button>
                        <Button onClick={deleteInstagramChannel} color="error" variant="contained">
                            Disconnect
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* WhatsApp Delete Confirmation Dialog */}
                <Dialog open={whatsappDeleteDialogOpen} onClose={() => setWhatsappDeleteDialogOpen(false)}>
                    <DialogTitle>Disconnect WhatsApp?</DialogTitle>
                    <DialogContent>
                        <Typography>
                            Are you sure you want to disconnect your WhatsApp Business account
                            ({whatsappToDelete?.display_phone_number || whatsappToDelete?.verified_name || 'this account'})?
                            You will no longer receive messages from this number.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setWhatsappDeleteDialogOpen(false)}>Cancel</Button>
                        <Button onClick={deleteWhatsAppChannel} color="error" variant="contained">
                            Disconnect
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Facebook Delete Confirmation Dialog */}
                <Dialog open={facebookDeleteDialogOpen} onClose={() => setFacebookDeleteDialogOpen(false)}>
                    <DialogTitle>Disconnect Facebook?</DialogTitle>
                    <DialogContent>
                        <Typography>
                            Are you sure you want to disconnect your Facebook Page
                            ({facebookToDelete?.page_name || 'this page'})?
                            You will no longer receive Messenger messages from this page.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setFacebookDeleteDialogOpen(false)}>Cancel</Button>
                        <Button onClick={deleteFacebookChannel} color="error" variant="contained">
                            Disconnect
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* WhatsApp Connect Modal (Manual Token Entry) */}
                <Dialog open={whatsappConnectDialogOpen} onClose={() => setWhatsappConnectDialogOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Connect WhatsApp Business</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Enter your WhatsApp Business API credentials from Meta Business Manager.
                        </Typography>
                        <TextField
                            fullWidth
                            label="Phone Number ID *"
                            placeholder="e.g., 123456789012345"
                            value={whatsappFormData.phone_number_id}
                            onChange={(e) => setWhatsappFormData(prev => ({ ...prev, phone_number_id: e.target.value }))}
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            fullWidth
                            label="WhatsApp Business Account ID"
                            placeholder="e.g., 987654321098765"
                            value={whatsappFormData.waba_id}
                            onChange={(e) => setWhatsappFormData(prev => ({ ...prev, waba_id: e.target.value }))}
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            fullWidth
                            label="Display Phone Number"
                            placeholder="e.g., +1 234 567 8900"
                            value={whatsappFormData.display_phone_number}
                            onChange={(e) => setWhatsappFormData(prev => ({ ...prev, display_phone_number: e.target.value }))}
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            fullWidth
                            label="Permanent Access Token *"
                            placeholder="Your System User access token"
                            value={whatsappFormData.access_token}
                            onChange={(e) => setWhatsappFormData(prev => ({ ...prev, access_token: e.target.value }))}
                            type="password"
                            sx={{ mb: 2 }}
                        />
                        <Alert severity="info" sx={{ mt: 1 }}>
                            Get these values from Meta Business Manager → System Users → Generate Token
                        </Alert>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setWhatsappConnectDialogOpen(false)}>Cancel</Button>
                        <Button
                            onClick={connectWhatsApp}
                            variant="contained"
                            disabled={whatsappConnecting}
                            startIcon={whatsappConnecting ? <CircularProgress size={16} /> : null}
                        >
                            {whatsappConnecting ? 'Connecting...' : 'Connect'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Widget Delete Confirmation Dialog */}
                <Dialog open={widgetDeleteDialogOpen} onClose={() => setWidgetDeleteDialogOpen(false)}>
                    <DialogTitle>Delete Web Widget?</DialogTitle>
                    <DialogContent>
                        <Typography>
                            Are you sure you want to delete your Web Widget configuration?
                            This will remove the widget from all websites where it's embedded.
                            All widget sessions will also be deleted.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setWidgetDeleteDialogOpen(false)}>Cancel</Button>
                        <Button onClick={deleteWidgetConfiguration} color="error" variant="contained">
                            Delete Widget
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Snackbar */}
                    {/* ========== POPUP CAMPAIGNS SECTION ========== */}
                    <Paper sx={{ p: 3, mt: 4, bgcolor: 'background.paper', borderRadius: 2 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <CampaignIcon color="primary" />
                                <Typography variant="h6">Popup Campaigns</Typography>
                            </Box>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => openCampaignDialog()}
                                size="small"
                            >
                                New Campaign
                            </Button>
                        </Box>
                        <Typography variant="body2" color="text.secondary" mb={2}>
                            Configure auto-popup campaigns for your widget. Campaigns will trigger the widget to open automatically.
                        </Typography>
                        
                        {campaignsLoading ? (
                            <Box display="flex" justifyContent="center" p={3}>
                                <CircularProgress size={30} />
                            </Box>
                        ) : campaigns.length === 0 ? (
                            <Alert severity="info">No campaigns yet. Create one to get started.</Alert>
                        ) : (
                            <List>
                                {campaigns.map((campaign) => (
                                    <ListItem 
                                        key={campaign.id} 
                                        sx={{ 
                                            mb: 1, 
                                            bgcolor: campaign.is_active ? 'action.hover' : 'transparent',
                                            borderRadius: 1,
                                            border: '1px solid',
                                            borderColor: 'divider'
                                        }}
                                    >
                                        <ListItemText
                                            primary={
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Typography variant="subtitle1">{campaign.name}</Typography>
                                                    <Chip 
                                                        label={campaign.is_active ? 'Active' : 'Paused'} 
                                                        size="small" 
                                                        color={campaign.is_active ? 'success' : 'default'}
                                                    />
                                                </Box>
                                            }
                                            secondary={
                                                <Box display="flex" alignItems="center" gap={2} mt={0.5}>
                                                    <Box display="flex" alignItems="center" gap={0.5}>
                                                        <TimerIcon fontSize="small" color="action" />
                                                        <Typography variant="caption">{campaign.delay_seconds}s delay</Typography>
                                                    </Box>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {campaign.impressions || 0} impressions
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                        <ListItemSecondaryAction>
                                            <Tooltip title={campaign.is_active ? 'Pause' : 'Activate'}>
                                                <Switch
                                                    checked={campaign.is_active}
                                                    onChange={() => toggleCampaign(campaign)}
                                                    size="small"
                                                />
                                            </Tooltip>
                                            <Tooltip title="Edit">
                                                <IconButton onClick={() => openCampaignDialog(campaign)} size="small">
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton onClick={() => setDeletingCampaign(campaign)} size="small" color="error">
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </Paper>
                    
                    {/* Campaign Dialog */}
                    <Dialog open={campaignDialogOpen} onClose={() => setCampaignDialogOpen(false)} maxWidth="sm" fullWidth>
                        <DialogTitle>{editingCampaign ? 'Edit Campaign' : 'New Campaign'}</DialogTitle>
                        <DialogContent>
                            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextField
                                    label="Campaign Name"
                                    value={campaignFormData.name}
                                    onChange={(e) => setCampaignFormData({...campaignFormData, name: e.target.value})}
                                    fullWidth
                                    required
                                />
                                <TextField
                                    label="Delay (seconds)"
                                    type="number"
                                    value={campaignFormData.delay_seconds}
                                    onChange={(e) => setCampaignFormData({...campaignFormData, delay_seconds: parseInt(e.target.value) || 5})}
                                    inputProps={{ min: 0, max: 300 }}
                                    helperText="Time to wait before opening widget (0-300 seconds)"
                                />
                                <TextField
                                    label="Popup Message (optional)"
                                    value={campaignFormData.popup_message}
                                    onChange={(e) => setCampaignFormData({...campaignFormData, popup_message: e.target.value})}
                                    multiline
                                    rows={2}
                                    helperText="Custom greeting message when popup appears"
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={campaignFormData.is_active}
                                            onChange={(e) => setCampaignFormData({...campaignFormData, is_active: e.target.checked})}
                                        />
                                    }
                                    label="Active"
                                />
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setCampaignDialogOpen(false)}>Cancel</Button>
                            <Button onClick={saveCampaign} variant="contained" disabled={!campaignFormData.name}>
                                {editingCampaign ? 'Update' : 'Create'}
                            </Button>
                        </DialogActions>
                    </Dialog>
                    
                    {/* Delete Campaign Confirmation */}
                    <Dialog open={!!deletingCampaign} onClose={() => setDeletingCampaign(null)}>
                        <DialogTitle>Delete Campaign?</DialogTitle>
                        <DialogContent>
                            <Typography>
                                Are you sure you want to delete "{deletingCampaign?.name}"? This action cannot be undone.
                            </Typography>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setDeletingCampaign(null)}>Cancel</Button>
                            <Button onClick={deleteCampaign} color="error" variant="contained">Delete</Button>
                        </DialogActions>
                    </Dialog>
                    {/* ========== END POPUP CAMPAIGNS SECTION ========== */}


                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={5000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert
                        onClose={() => setSnackbar({ ...snackbar, open: false })}
                        severity={snackbar.severity}
                        sx={{ width: '100%' }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>

            {/* Right Navigation Drawer */}
            <LeftNavDrawer
                anchor="right"
                open={navSidebarOpen}
                onClose={() => setNavSidebarOpen(false)}
                onLogout={handleLogout}
                onOpenFiles={() => navigate('/files')}
                onOpenFAQManagement={() => navigate('/faq')}
                onOpenChatwootSettings={() => navigate('/inbox')}
                onOpenEcommerceIntegrations={() => navigate('/my-stores')}
                onOpenUpload={() => navigate('/upload')}
                onOpenUsageBilling={() => navigate('/billing')}
            />
        </Box>
    );
}

export default ChannelsSettings;
